import { useMemo, useState } from 'react';
import {
  BadgeDollarSign,
  MousePointerClick,
  Target,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { StatTile } from '@/components/marketing/MetricTile';
import { TrendChart } from '@/components/marketing/TrendChart';
import {
  ChannelStatusNotice,
  resolveChannelStatus,
} from '@/components/marketing/ChannelStatusNotice';
import { CHART_SERIES } from '@/lib/brand';
import { formatCurrency } from '@/lib/formatters';
import { PLATFORM_ICONS, PLATFORM_LABELS } from '@/features/social/constants';
import {
  AD_PLATFORM_LABELS,
  SOCIAL_PLATFORMS,
  useAdsAnalytics,
  useSocialAnalytics,
  type AdCampaignRow,
  type AdPlatform,
  type AdPlatformData,
  type SocialPlatform,
  type SocialPlatformData,
} from '@/features/marketing/hooks';
import {
  fillDailyGapsInRange,
  formatCompactNumber,
  formatRatioPercent,
  pctChange,
  pctChangeInverted,
  type AnalyticsWindow,
} from '@/features/marketing/range';
import { cn } from '@/lib/utils';

const PAID_PLATFORMS: AdPlatform[] = ['meta_ads', 'google_ads', 'amazon_ads'];

const AD_PLATFORM_COLORS: Record<AdPlatform, string> = {
  meta_ads: CHART_SERIES[3],
  google_ads: CHART_SERIES[2],
  amazon_ads: CHART_SERIES[4],
  tiktok: CHART_SERIES[1],
};

interface MarketingModuleProps {
  period: AnalyticsWindow;
  comparisonLabel: string;
}

export function MarketingModule({ period, comparisonLabel }: MarketingModuleProps) {
  const ads = useAdsAnalytics(period);
  const social = useSocialAnalytics(period);
  const [campaignPlatform, setCampaignPlatform] = useState<AdPlatform | 'all'>('all');

  const adsLoading = ads.isLoading;
  const socialLoading = social.isLoading;

  const tiktokAds = ads.data?.platforms.find((p) => p.platform === 'tiktok' && p.connected);

  const adsStatus = resolveChannelStatus({
    isLoading: ads.isLoading,
    error: ads.error,
    connected: ads.data?.connected,
  });
  const socialStatus = resolveChannelStatus({
    isLoading: social.isLoading,
    error: social.error,
    connected: social.data?.connected,
    awaitingFirstSync: social.data?.platforms.some((p) => p.awaitingFirstSync),
  });

  const adDaily = useMemo(() => {
    const rows = ads.data?.daily ?? [];
    return fillDailyGapsInRange(rows, period.currentStart, period.currentEnd, (day) => ({
      day,
      impressions: 0,
      clicks: 0,
      spendCents: 0,
      conversions: 0,
      revenueCents: 0,
    }));
  }, [ads.data?.daily, period.currentStart, period.currentEnd]);

  const campaigns = (ads.data?.campaigns ?? []).filter((row) =>
    campaignPlatform === 'all' ? true : row.platform === campaignPlatform,
  );

  const campaignPlatforms: Array<AdPlatform | 'all'> = [
    'all',
    ...PAID_PLATFORMS,
    ...(tiktokAds ? (['tiktok'] as AdPlatform[]) : []),
  ];

  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-lg font-medium tracking-headline text-foreground">Marketing</h2>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-medium text-foreground">Paid channels</h3>
        <ChannelStatusNotice
          status={adsStatus}
          channel="Ad platforms"
          error={ads.error instanceof Error ? ads.error.message : null}
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {PAID_PLATFORMS.map((id) => (
            <PaidChannelCard
              key={id}
              data={ads.data?.platforms.find((p) => p.platform === id) ?? emptyAdPlatform(id)}
              loading={adsLoading}
              comparisonLabel={comparisonLabel}
            />
          ))}
        </div>
        {tiktokAds && (
          <PaidChannelCard
            data={tiktokAds}
            loading={adsLoading}
            comparisonLabel={comparisonLabel}
          />
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Ad performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4 grid grid-cols-2 gap-4 xl:grid-cols-4">
            <StatTile
              label="Spend"
              icon={BadgeDollarSign}
              loading={adsLoading}
              value={formatCurrency(ads.data?.totals.spendCents ?? 0, { whole: true })}
              delta={pctChange(ads.data?.totals.spendCents ?? 0, ads.data?.previous.spendCents ?? 0)}
              deltaLabel={comparisonLabel}
            />
            <StatTile
              label="Conversions"
              icon={Target}
              loading={adsLoading}
              value={formatCompactNumber(ads.data?.totals.conversions ?? 0)}
              delta={pctChange(
                ads.data?.totals.conversions ?? 0,
                ads.data?.previous.conversions ?? 0,
              )}
              deltaLabel={comparisonLabel}
            />
            <StatTile
              label="ROAS"
              icon={BadgeDollarSign}
              loading={adsLoading}
              value={`${(ads.data?.totals.roas ?? 0).toFixed(2)}×`}
              delta={pctChange(ads.data?.totals.roas ?? 0, ads.data?.previous.roas ?? 0)}
              deltaLabel={comparisonLabel}
            />
            <StatTile
              label="CTR"
              icon={MousePointerClick}
              loading={adsLoading}
              value={formatRatioPercent(ads.data?.totals.ctr ?? 0)}
              delta={pctChange(ads.data?.totals.ctr ?? 0, ads.data?.previous.ctr ?? 0)}
              deltaLabel={comparisonLabel}
            />
          </div>
          <TrendChart
            data={adDaily}
            series={[
              {
                key: 'spendCents',
                label: 'Spend',
                color: CHART_SERIES[0],
                kind: 'bar',
                format: (value) => formatCurrency(value, { whole: true }),
              },
              {
                key: 'conversions',
                label: 'Conversions',
                color: CHART_SERIES[2],
                kind: 'line',
                axis: 'right',
              },
            ]}
            formatLeftAxis={(value) => formatCurrency(value, { compact: true, whole: true })}
            height={280}
          />
        </CardContent>
      </Card>

      <div className="space-y-3">
        <h3 className="text-sm font-medium text-foreground">Organic audience</h3>
        <ChannelStatusNotice
          status={socialStatus}
          channel="Social accounts"
          error={social.error instanceof Error ? social.error.message : null}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {SOCIAL_PLATFORMS.map((platform) => (
            <OrganicCard
              key={platform}
              platform={platform}
              data={social.data?.platforms.find((p) => p.platform === platform)}
              loading={socialLoading}
              comparisonLabel={comparisonLabel}
            />
          ))}
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <CardTitle>Ad campaigns</CardTitle>
          </div>
          <div className="flex flex-wrap gap-1">
            {campaignPlatforms.map((id) => (
              <Button
                key={id}
                size="sm"
                variant={campaignPlatform === id ? 'default' : 'outline'}
                onClick={() => setCampaignPlatform(id)}
              >
                {id === 'all' ? 'All' : AD_PLATFORM_LABELS[id]}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <CampaignTable rows={campaigns} loading={adsLoading} />
        </CardContent>
      </Card>
    </section>
  );
}

function emptyAdPlatform(platform: AdPlatform): AdPlatformData {
  const totals = {
    impressions: 0,
    clicks: 0,
    spendCents: 0,
    conversions: 0,
    revenueCents: 0,
    ctr: 0,
    cpcCents: 0,
    cpaCents: 0,
    roas: 0,
  };
  return {
    platform,
    connected: false,
    totals,
    previous: totals,
    daily: [],
    campaigns: [],
  };
}

function PaidChannelCard({
  data,
  loading,
  comparisonLabel,
}: {
  data: AdPlatformData;
  loading: boolean;
  comparisonLabel: string;
}) {
  const totals = data.totals;
  return (
    <Card className={cn(!data.connected && 'border-dashed bg-card/50')}>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-foreground">
          {AD_PLATFORM_LABELS[data.platform]}
        </CardTitle>
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: data.connected ? AD_PLATFORM_COLORS[data.platform] : undefined }}
          title={data.connected ? 'Has data this period' : 'Not connected'}
        />
      </CardHeader>
      <CardContent className="space-y-3">
        {!data.connected && !loading ? (
          <p className="text-sm text-muted-foreground">Not connected</p>
        ) : (
          <>
            <div>
              <p className="text-2xl font-medium tabular-nums tracking-headline text-foreground">
                {loading ? '—' : formatCurrency(totals.spendCents, { whole: true })}
              </p>
              <p className="text-xs text-muted-foreground">Spend</p>
            </div>
            <dl className="grid grid-cols-2 gap-2 text-xs">
              <Metric
                label="ROAS"
                value={loading ? '—' : `${totals.roas.toFixed(2)}×`}
                delta={pctChange(totals.roas, data.previous.roas)}
                deltaLabel={comparisonLabel}
              />
              <Metric
                label="Conversions"
                value={loading ? '—' : formatCompactNumber(totals.conversions)}
                delta={pctChange(totals.conversions, data.previous.conversions)}
                deltaLabel={comparisonLabel}
              />
              <Metric
                label="CPC"
                value={loading ? '—' : formatCurrency(totals.cpcCents)}
                delta={pctChangeInverted(totals.cpcCents, data.previous.cpcCents)}
                deltaLabel={comparisonLabel}
              />
              <Metric
                label="CTR"
                value={loading ? '—' : formatRatioPercent(totals.ctr)}
                delta={pctChange(totals.ctr, data.previous.ctr)}
                deltaLabel={comparisonLabel}
              />
            </dl>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function OrganicCard({
  platform,
  data,
  loading,
  comparisonLabel,
}: {
  platform: SocialPlatform;
  data: SocialPlatformData | undefined;
  loading: boolean;
  comparisonLabel: string;
}) {
  const Icon = PLATFORM_ICONS[platform];
  const connected = !!data?.connected;
  const totals = data?.totals;

  return (
    <Card className={cn(!connected && 'border-dashed bg-card/50')}>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {PLATFORM_LABELS[platform]}
        </CardTitle>
        {data?.awaitingFirstSync && (
          <Badge variant="muted">Syncing</Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {!connected && !loading ? (
          <p className="text-sm text-muted-foreground">Not connected</p>
        ) : (
          <>
            <div>
              <p className="text-2xl font-medium tabular-nums tracking-headline text-foreground">
                {loading ? '—' : formatCompactNumber(totals?.followers ?? 0)}
              </p>
              <p className="text-xs text-muted-foreground">Followers</p>
            </div>
            <dl className="grid grid-cols-2 gap-2 text-xs">
              <Metric
                label="Growth"
                value={
                  loading
                    ? '—'
                    : `${(totals?.followerGrowth ?? 0) >= 0 ? '+' : ''}${(totals?.followerGrowth ?? 0).toLocaleString()}`
                }
                delta={pctChange(totals?.followerGrowth ?? 0, data?.previous.followerGrowth ?? 0)}
                deltaLabel={comparisonLabel}
              />
              <Metric
                label="Engagements"
                value={loading ? '—' : formatCompactNumber(totals?.engagements ?? 0)}
                delta={pctChange(totals?.engagements ?? 0, data?.previous.engagements ?? 0)}
                deltaLabel={comparisonLabel}
              />
              <Metric
                label="Rate"
                value={loading ? '—' : formatRatioPercent(totals?.engagementRate ?? 0)}
                delta={pctChange(totals?.engagementRate ?? 0, data?.previous.engagementRate ?? 0)}
                deltaLabel={comparisonLabel}
              />
              <Metric
                label={platform === 'youtube' ? 'Views' : 'Reach'}
                value={
                  loading
                    ? '—'
                    : formatCompactNumber(
                        platform === 'youtube' || platform === 'tiktok'
                          ? (totals?.videoViews ?? 0)
                          : (totals?.reach ?? totals?.impressions ?? 0),
                      )
                }
              />
            </dl>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function CampaignTable({ rows, loading }: { rows: AdCampaignRow[]; loading: boolean }) {
  if (loading) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Loading campaigns…</p>;
  }
  if (rows.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No campaign activity in this window.
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Campaign</TableHead>
          <TableHead>Platform</TableHead>
          <TableHead className="text-right">Spend</TableHead>
          <TableHead className="text-right">Impr.</TableHead>
          <TableHead className="text-right">Clicks</TableHead>
          <TableHead className="text-right">CTR</TableHead>
          <TableHead className="text-right">Conv.</TableHead>
          <TableHead className="text-right">Revenue</TableHead>
          <TableHead className="text-right">ROAS</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.slice(0, 40).map((row) => {
          const ctr = row.impressions > 0 ? row.clicks / row.impressions : 0;
          return (
            <TableRow key={row.key}>
              <TableCell className="max-w-[260px] truncate font-medium" title={row.name}>
                {row.name}
              </TableCell>
              <TableCell>
                <Badge variant="secondary">{AD_PLATFORM_LABELS[row.platform]}</Badge>
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatCurrency(row.spendCents, { whole: true })}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatCompactNumber(row.impressions)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatCompactNumber(row.clicks)}
              </TableCell>
              <TableCell className="text-right tabular-nums">{formatRatioPercent(ctr)}</TableCell>
              <TableCell className="text-right tabular-nums">
                {formatCompactNumber(row.conversions)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatCurrency(row.revenueCents, { whole: true })}
              </TableCell>
              <TableCell className="text-right tabular-nums">{row.roas.toFixed(2)}×</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function Metric({
  label,
  value,
  delta,
  deltaLabel,
}: {
  label: string;
  value: string;
  delta?: number | null;
  deltaLabel?: string;
}) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular-nums text-foreground">{value}</dd>
      {delta != null && (
        <p
          className={cn(
            'tabular-nums',
            delta > 0 ? 'text-bright-moss-dark' : delta < 0 ? 'text-fresh-salmon-dark' : 'text-muted-foreground',
          )}
        >
          {delta > 0 ? '+' : ''}
          {(delta * 100).toFixed(1)}%
          {deltaLabel ? <span className="ml-1 text-muted-foreground">{deltaLabel}</span> : null}
        </p>
      )}
    </div>
  );
}
